import env from '../config/env';

/**
 * Reading a pasted dispatch text with the backend's parser
 * (POST /calls/parse-text), and telling it how the call went after
 * (POST /calls/parse-text/feedback). The shapes match the backend's
 * dispatch-parse.dto.ts.
 */

/** Every field the parser can fill, by the backend's names. */
export type DispatchParseField =
  | 'pickup'
  | 'pickupCity'
  | 'crossStreet'
  | 'destination'
  | 'vehicle.year'
  | 'vehicle.make'
  | 'vehicle.model'
  | 'vehicle.color'
  | 'plate'
  | 'vin'
  | 'weightClass'
  | 'priority'
  | 'contactName'
  | 'contactPhone'
  | 'eventNumber'
  | 'receivedAt'
  | 'reason'
  | 'accountId'
  | 'accountNameAsSent'
  | 'driverId'
  | 'notes';

export interface ParsedCallFields {
  pickup: string | null;
  pickupCity: string | null;
  crossStreet: string | null;
  destination: string | null;
  vehicle: {
    year: number | null;
    make: string | null;
    model: string | null;
    color: string | null;
  };
  plate: string | null;
  vin: string | null;
  weightClass: 'LIGHT' | 'MEDIUM' | 'HEAVY' | null;
  priority: 'NORMAL' | 'HIGH' | 'EMERGENCY';
  contactName: string | null;
  /** Digits, with +1 in front of a 10-digit US number. */
  contactPhone: string | null;
  eventNumber: string | null;
  /** ISO 8601 UTC. */
  receivedAt: string | null;
  reason: string | null;
  /** A Customer id of the company's. */
  accountId: string | null;
  accountNameAsSent: string | null;
  /** A Driver id (not a user id) of the company's. */
  driverId: string | null;
  notes: string[];
}

export interface ParseTextResponse {
  fields: ParsedCallFields;
  model: string;
  latencyMs: number;
  /** The company's time zone, which receivedAt was read in. */
  timeZone?: string;
}

export interface ParseFeedback {
  fieldsChanged: DispatchParseField[];
  fieldsFilledByAi: DispatchParseField[];
  fallbackUsed: boolean;
  dispatched: boolean;
}

export interface RequestAuth {
  token?: string | null;
  companyId?: string | null;
}

/** The parse is given up on after this, and the rules parser reads it. */
export const PARSE_CLIENT_TIMEOUT_MS = 10_000;

/** The backend takes at most this many characters. */
export const PARSE_TEXT_MAX = 4000;

function headersFor({ token, companyId }: RequestAuth) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (companyId) headers['x-company-id'] = companyId;
  return headers;
}

/**
 * The text's fields, or a thrown error for anything else: a non-2xx (503
 * PARSE_UNAVAILABLE included), a network failure, or no answer within
 * `timeoutMs`.
 */
export async function parseDispatchText(
  text: string,
  auth: RequestAuth,
  timeoutMs = PARSE_CLIENT_TIMEOUT_MS
): Promise<ParseTextResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${env.apiUrl}/calls/parse-text`, {
      method: 'POST',
      headers: headersFor(auth),
      body: JSON.stringify({ text: text.slice(0, PARSE_TEXT_MAX) }),
      signal: controller.signal,
    });
    if (!response.ok) {
      throw Object.assign(new Error(`Parse failed: ${response.status}`), {
        status: response.status,
      });
    }
    const body = (await response.json()) as ParseTextResponse;
    if (!body?.fields) throw new Error('Parse answered with no fields');
    return body;
  } finally {
    clearTimeout(timer);
  }
}

/** How the form went after a parse. The backend only logs it. */
export async function sendParseFeedback(
  feedback: ParseFeedback,
  auth: RequestAuth
): Promise<void> {
  const response = await fetch(`${env.apiUrl}/calls/parse-text/feedback`, {
    method: 'POST',
    headers: headersFor(auth),
    body: JSON.stringify(feedback),
  });
  if (!response.ok) throw new Error(`Feedback failed: ${response.status}`);
}
